// webgl-lifecycle.ts
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import SceneManager from '../../core/SceneManager';
import { global } from '../../core/GlobalState';
import { OrbManager } from '../../orbs/OrbManager';
import { OrbConnectionManager } from '../connections/OrbConnectionManager';
import { clearOrbSpawnQueue, resetOrbSpawnThrottles } from '../physics/OrbSpawnScheduler';
import { forceCleanReload, markReloadPending, clearReloadFlag } from './CrashRecovery';
export function registerWebGLContextLifecycle(renderer, handlers) {
    var _a, _b;
    const onContextRestored = typeof handlers === 'function' ? handlers : handlers === null || handlers === void 0 ? void 0 : handlers.onContextRestored;
    const onContextLost = typeof handlers === 'function' ? undefined : handlers === null || handlers === void 0 ? void 0 : handlers.onContextLost;
    // Expose a flag your animate() can check
    global.__webglLost = false;
    const canvas = renderer.domElement;
    const gl = renderer.getContext();
    const loseContextExtension = (_b = (_a = gl === null || gl === void 0 ? void 0 : gl.getExtension) === null || _a === void 0 ? void 0 : _a.call(gl, 'WEBGL_lose_context')) !== null && _b !== void 0 ? _b : null;
    let watchdogHandle = null;
    let pressureHandle = null;
    let reloadTimer = null;
    let lossCount = 0;
    let lastPressureStep = 0;
    const PRESSURE_CHECK_MS = 5000;
    const PRESSURE_COOLDOWN_MS = 15000;
    const TEXTURE_PRESSURE_THRESHOLD = 1200;
    const GEOMETRY_PRESSURE_THRESHOLD = 2000;
    const scheduleForcedReload = (reason) => {
        if (typeof window === 'undefined')
            return;
        if (reloadTimer !== null)
            return;
        reloadTimer = window.setTimeout(() => {
            reloadTimer = null;
            void forceCleanReload(reason).catch(() => window.location.reload());
        }, 8000);
    };
    const cancelForcedReload = () => {
        if (reloadTimer === null || typeof window === 'undefined')
            return;
        window.clearTimeout(reloadTimer);
        reloadTimer = null;
    };
    const attemptRestore = () => {
        if (!loseContextExtension || typeof loseContextExtension.restoreContext !== 'function')
            return;
        try {
            loseContextExtension.restoreContext();
        }
        catch (err) {
            console.warn('[WebGL] Failed to trigger context restore', err);
            void forceCleanReload('restore-extension-failed');
        }
    };
    const downscalePortalTargets = (scale, minResolution = 256) => {
        try {
            OrbManager.getInstance().getAllOrbs().forEach(orb => {
                var _a;
                if (((_a = orb.orbConfig) === null || _a === void 0 ? void 0 : _a.isPortal) && orb.orbConfig.portalResolution) {
                    const next = Math.max(minResolution, Math.floor(orb.orbConfig.portalResolution * scale));
                    if (next < orb.orbConfig.portalResolution) {
                        orb.orbConfig.portalResolution = next;
                        if (orb.renderTarget) {
                            orb.renderTarget.setSize(next, next);
                        }
                    }
                }
            });
        }
        catch (err) {
            console.warn('[WebGL] Failed to downscale portals', err);
        }
    };
    const reduceRendererPixelRatio = (delta) => {
        global.maxRendererPixelRatio = Math.max(1, global.maxRendererPixelRatio - delta);
        if (typeof window !== 'undefined') {
            const px = Math.min(window.devicePixelRatio || 1, global.maxRendererPixelRatio);
            renderer.setPixelRatio(px);
            renderer.setSize(window.innerWidth, window.innerHeight, false);
        }
    };
    const startWatchdog = () => {
        if (!(gl === null || gl === void 0 ? void 0 : gl.isContextLost) || typeof window === 'undefined')
            return;
        if (watchdogHandle !== null)
            return;
        watchdogHandle = window.setInterval(() => {
            try {
                if (global.__webglLost)
                    return;
                if (gl.isContextLost && gl.isContextLost()) {
                    console.warn('[WebGL] Context loss detected via watchdog');
                    global.__webglLost = true;
                    resetOrbSpawnThrottles();
                    attemptRestore();
                }
            }
            catch (err) {
                console.warn('[WebGL] Watchdog check failed', err);
            }
        }, 4000);
    };
    const stopWatchdog = () => {
        if (watchdogHandle === null || typeof window === 'undefined')
            return;
        window.clearInterval(watchdogHandle);
        watchdogHandle = null;
    };
    const startPressureGuard = () => {
        if (typeof window === 'undefined')
            return;
        if (pressureHandle !== null)
            return;
        pressureHandle = window.setInterval(() => {
            var _a;
            if (global.__webglLost)
                return;
            const info = (_a = renderer.info) === null || _a === void 0 ? void 0 : _a.memory;
            if (!info)
                return;
            const now = Date.now();
            if (now - lastPressureStep < PRESSURE_COOLDOWN_MS)
                return;
            if (info.textures >= TEXTURE_PRESSURE_THRESHOLD || info.geometries >= GEOMETRY_PRESSURE_THRESHOLD) {
                lastPressureStep = now;
                console.warn('[WebGL] GPU pressure guard triggered', {
                    textures: info.textures,
                    geometries: info.geometries,
                });
                reduceRendererPixelRatio(0.1);
                downscalePortalTargets(0.85);
            }
        }, PRESSURE_CHECK_MS);
    };
    startWatchdog();
    startPressureGuard();
    // Optional: simulate in dev
    // const loseExt = renderer.getContext().getExtension('WEBGL_lose_context');
    canvas.addEventListener('webglcontextlost', (e) => {
        var _a, _b;
        console.warn('[WebGL] context lost');
        e.preventDefault(); // request restoration
        global.__webglLost = true;
        clearOrbSpawnQueue();
        markReloadPending('webgl-context-lost');
        try {
            onContextLost === null || onContextLost === void 0 ? void 0 : onContextLost();
        }
        catch (err) {
            console.warn('[WebGL] context lost callback failed', err);
        }
        lossCount += 1;
        if (lossCount >= 1) {
            reduceRendererPixelRatio(0.1);
            downscalePortalTargets(0.85);
        }
        // Stop using FBOs immediately to help restoration succeed
        try {
            (_b = (_a = global.composer) === null || _a === void 0 ? void 0 : _a.dispose) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
        catch (_c) { }
        // If you keep custom render targets (portals), dispose them here too:
        try {
            OrbManager.getInstance().getAllOrbs().forEach(orb => {
                if (orb.renderTarget) {
                    orb.renderTarget.dispose();
                    orb.renderTarget = null;
                }
            });
        }
        catch (_d) { }
        if (typeof window !== 'undefined') {
            window.setTimeout(attemptRestore, 250);
            scheduleForcedReload('webgl-restore-timeout');
        }
        else {
            attemptRestore();
        }
        startWatchdog();
    });
    canvas.addEventListener('webglcontextrestored', () => {
        var _a;
        console.warn('[WebGL] context restored');
        cancelForcedReload();
        clearReloadFlag();
        // Reapply renderer sizes & pixel ratio (context lost resets GL state)
        const w = window.innerWidth;
        const h = window.innerHeight;
        const maxPixelRatio = global.maxRendererPixelRatio || 1.5;
        const px = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
        renderer.setPixelRatio(px);
        renderer.setSize(w, h, false);
        try {
            (_a = renderer.resetState) === null || _a === void 0 ? void 0 : _a.call(renderer);
        }
        catch (_b) { }
        // Rebuild post-processing
        global.composer = new EffectComposer(renderer);
        if (typeof global.composer.setPixelRatio === 'function') {
            global.composer.setPixelRatio(px);
        }
        global.renderPass = new RenderPass(global.scene, global.camera);
        global.composer.addPass(global.renderPass);
        global.outlinePass = new OutlinePass(new THREE.Vector2(w, h), global.scene, global.camera);
        global.composer.addPass(global.outlinePass);
        if (typeof global.outlinePass.setSize === 'function') {
            global.outlinePass.setSize(w, h);
        }
        // Mark all materials/textures in ALL managed scenes dirty so they re-upload to GPU
        const sm = SceneManager.getInstance();
        for (const name of sm.listScenes()) {
            const s = sm.getScene(name);
            s.traverse((obj) => {
                const mat = obj.material;
                if (!mat)
                    return;
                const markTex = (m) => {
                    for (const k in m) {
                        const v = m[k];
                        if (v && v.isTexture) {
                            v.needsUpdate = true;
                        }
                    }
                    m.needsUpdate = true;
                };
                if (Array.isArray(mat))
                    mat.forEach(markTex);
                else
                    markTex(mat);
            });
        }
        // Recreate custom render targets (e.g., portals)
        try {
            OrbManager.getInstance().getAllOrbs().forEach(orb => {
                if (typeof orb.recreateWebGLResources === 'function') {
                    orb.recreateWebGLResources(renderer);
                }
            });
        }
        catch (_c) { }
        try {
            OrbConnectionManager.getInstance().updateResolution();
        }
        catch (_d) { }
        global.__webglLost = false;
        resetOrbSpawnThrottles();
        stopWatchdog();
        try {
            onContextRestored === null || onContextRestored === void 0 ? void 0 : onContextRestored();
        }
        catch (err) {
            console.error('[WebGL] context restore callback failed', err);
        }
        lossCount = 0;
    });
}
